import React, { useCallback, useEffect, useState } from 'react';

import { PAGE_SIZE_SM } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ClickableIcon, ErrorResponse, Modal, Spinner } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { SearchProps } from '~components/form-fields/search/props';
import { faMagnifyingGlass } from '~components/icon';
import { Column, Row, TableView } from '~components/table-view';

import {
  useGetDashboardCmWorkersQuery,
  useGetDashboardOverUtilisedCmQuery,
  useGetDashboardOverUtilisedQuery,
  useGetDashboardUnderUtilisedCmQuery,
} from '~support-at-home/actions/dashboardBudgetsExpanded';
import { ExpandedTable } from '~support-at-home/models/dashboard';
import { cmClientColumns, cmWorkersColumns, overUtilisedColumns } from './utils';

interface TableConfig {
  title: string;
  columns: Column[];
  searchField: string;
  searchPlaceholder: string;
}

const TABLE_CONFIG: Record<string, TableConfig> = {
  'over-utilised': {
    title: 'All Over-Utilised Budgets',
    columns: overUtilisedColumns,
    searchField: 'client_name',
    searchPlaceholder: 'Client name...',
  },
  'over-utilised-cm': {
    title: 'All Over-Utilised CM',
    columns: cmClientColumns,
    searchField: 'client_name',
    searchPlaceholder: 'Client name...',
  },
  'under-utilised-cm': {
    title: 'All Under-Utilised CM',
    columns: cmClientColumns,
    searchField: 'client_name',
    searchPlaceholder: 'Client name...',
  },
  'cm-workers': {
    title: 'All CM Workers',
    columns: cmWorkersColumns,
    searchField: 'worker_name',
    searchPlaceholder: 'Worker name...',
  },
};

interface ComponentProps {
  expandedTable: ExpandedTable;
  quarter: number;
  year: number;
  onClose: () => void;
}

const DashboardTableModal: React.FC<ComponentProps> = ({ expandedTable, quarter, year, onClose }) => {
  const isOpen = expandedTable !== null;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const handleClose = () => {
    setCurrentPage(1);
    setSearchText('');
    setSearchBarOpen(false);
    onClose();
  };

  // Defensive reset: if a future caller switches directly between tables (non-null → non-null),
  // stale searchText would be sent under the new table's searchField key. Reset on every change.
  useEffect(() => {
    if (expandedTable) {
      setCurrentPage(1);
      setSearchText('');
      setSearchBarOpen(false);
    }
  }, [expandedTable]);

  const offset = (currentPage - 1) * PAGE_SIZE_SM;
  const config = expandedTable ? TABLE_CONFIG[expandedTable] : null;

  const commonParams = {
    quarter,
    year,
    limit: PAGE_SIZE_SM,
    offset,
    [config?.searchField ?? 'client_name']: searchText || undefined,
  };
  const skip = !isOpen;

  const { data: overUtilisedData, isLoading: l1, isFetching: f1, error: e1 } = useGetDashboardOverUtilisedQuery(
    commonParams,
    { skip: skip || expandedTable !== 'over-utilised' },
  );
  const { data: overUtilisedCmData, isLoading: l2, isFetching: f2, error: e2 } = useGetDashboardOverUtilisedCmQuery(
    commonParams,
    { skip: skip || expandedTable !== 'over-utilised-cm' },
  );
  const { data: underUtilisedCmData, isLoading: l3, isFetching: f3, error: e3 } = useGetDashboardUnderUtilisedCmQuery(
    commonParams,
    { skip: skip || expandedTable !== 'under-utilised-cm' },
  );
  const { data: cmWorkersData, isLoading: l4, isFetching: f4, error: e4 } = useGetDashboardCmWorkersQuery(
    commonParams,
    { skip: skip || expandedTable !== 'cm-workers' },
  );

  const expandedDataMap = {
    'over-utilised': overUtilisedData,
    'over-utilised-cm': overUtilisedCmData,
    'under-utilised-cm': underUtilisedCmData,
    'cm-workers': cmWorkersData,
  };
  const activeData = expandedTable ? expandedDataMap[expandedTable] : undefined;

  const isLoading = l1 || l2 || l3 || l4;
  const isFetching = f1 || f2 || f3 || f4;
  const error = e1 || e2 || e3 || e4;

  const handleSearch = useCallback(
    (values: Record<string, unknown>) => {
      setSearchText((values[config?.searchField ?? 'client_name'] as string) ?? '');
      setCurrentPage(1);
    },
    [config?.searchField],
  );

  const searchFields: SearchProps = config
    ? {
        [config.searchField]: {
          defaultShow: true,
          type: 'text-like',
          field: config.searchField,
          caption: config.searchField === 'worker_name' ? 'Worker' : 'Client',
          placeholder: config.searchPlaceholder,
          persist: true,
        },
      }
    : {};

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) handleClose();
      }}
      header={config?.title ?? ''}
      size="xl"
      footer={
        <Button color="secondary" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div>
        <div className="d-flex justify-content-end mb-2">
          <ClickableIcon
            icon={faMagnifyingGlass}
            id="dashboard-expand-search-toggle"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
            toolTip="Toggle Search"
            ariaLabel="Toggle search"
            ariaExpanded={searchBarOpen}
          />
        </div>
        <SearchBar
          id="dashboard-expand-searchbar"
          isOpen={searchBarOpen}
          fields={searchFields}
          onSubmit={handleSearch}
          storageKey="dashboard-expand-modal"
        />
        <ErrorResponse message={error ? getErrorMessage(error) : undefined} />
        {isLoading && (
          <div className="loading" role="status" aria-live="polite">
            <Spinner loading type="beat" size={10} />
            Loading...
          </div>
        )}
        {!isLoading && config && (
          <TableView
            id="dashboard-expand-table"
            data={(activeData?.rows ?? []) as Row[]}
            columns={config.columns}
            isLoading={isFetching}
            allowPagination
            paginationType="pages"
            showCount
            currentPage={currentPage}
            pageSize={PAGE_SIZE_SM}
            totalCount={activeData?.total ?? 0}
            onPageChange={setCurrentPage}
            readonly
          />
        )}
      </div>
    </Modal>
  );
};

export default DashboardTableModal;
